import { useEffect, useState } from 'react';
import { BarChart3, Cookie, ShieldCheck, X } from 'lucide-react';

const storageKey = 'jgl_cookie_consent_v1';
const defaultPreferences = { necessary: true, analytics: false };
// The GA4 measurement ID is public configuration, not a secret. Keep the
// environment variable override for portability, while providing the
// production stream as a fallback for hosts that do not expose Vite variables
// during their build step.
const analyticsId = String(
  import.meta.env.VITE_GOOGLE_ANALYTICS_ID || 'G-6V5J5ZFMEK',
).trim();

function activateGoogleAnalytics() {
  if (!/^G-[A-Z0-9]+$/i.test(analyticsId) || document.getElementById('jgl-google-analytics')) return;
  window[`ga-disable-${analyticsId}`] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(){ window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', analyticsId, { anonymize_ip: true, send_page_view: true });
  const script = document.createElement('script');
  script.id = 'jgl-google-analytics';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(analyticsId)}`;
  document.head.appendChild(script);
}

function activateApprovedScripts(preferences) {
  window.jglConsent = preferences;
  if (preferences.analytics) {
    activateGoogleAnalytics();
    document.querySelectorAll('script[type="text/plain"][data-cookie-category="analytics"]').forEach((blockedScript) => {
      if (blockedScript.dataset.activated === 'true') return;
      const script = document.createElement('script');
      for (const attribute of blockedScript.attributes) {
        if (!['type', 'data-cookie-category', 'data-activated'].includes(attribute.name)) script.setAttribute(attribute.name, attribute.value);
      }
      script.textContent = blockedScript.textContent;
      blockedScript.dataset.activated = 'true';
      blockedScript.after(script);
    });
  } else if (analyticsId) window[`ga-disable-${analyticsId}`] = true;
  window.dispatchEvent(new CustomEvent('jgl:consent-updated', { detail: preferences }));
}

function readPreferences() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey));
    if (saved && typeof saved.analytics === 'boolean') return { necessary: true, analytics: saved.analytics };
  } catch {
    return null;
  }
  return null;
}

export function ConsentManager() {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);

  useEffect(() => {
    const saved = readPreferences();
    if (saved) {
      setPreferences(saved);
      activateApprovedScripts(saved);
    } else {
      activateApprovedScripts(defaultPreferences);
      setVisible(true);
    }
    const reopen = () => { setCustomizing(true); setVisible(true); };
    window.addEventListener('jgl:open-consent', reopen);
    return () => window.removeEventListener('jgl:open-consent', reopen);
  }, []);

  function save(nextPreferences) {
    const approved = { necessary: true, analytics: Boolean(nextPreferences.analytics) };
    window.localStorage.setItem(storageKey, JSON.stringify(approved));
    setPreferences(approved);
    activateApprovedScripts(approved);
    setVisible(false);
    setCustomizing(false);
  }

  if (!visible) return null;

  return <aside className="consent-panel" role="dialog" aria-modal="true" aria-labelledby="consent-title">
    <div className="consent-panel-icon"><Cookie size={27}/></div>
    <div className="consent-panel-copy">
      <p className="eyebrow">Privacy choices</p>
      <h2 id="consent-title">Your privacy. Your decision.</h2>
      <p>We use necessary browser storage to remember security and privacy choices. Optional analytics remain blocked unless you allow them. We do not use advertising cookies.</p>
      {customizing && <div className="consent-options">
        <label><span><ShieldCheck size={19}/><strong>Necessary storage</strong><small>Required for security, preferences, and essential website operation.</small></span><input type="checkbox" checked disabled aria-label="Necessary storage is always active"/></label>
        <label><span><BarChart3 size={19}/><strong>Optional analytics</strong><small>Allows approved measurement tools to help us understand website use.</small></span><input type="checkbox" checked={preferences.analytics} onChange={(event)=>setPreferences({...preferences,analytics:event.target.checked})} aria-label="Allow optional analytics"/></label>
      </div>}
      <div className="consent-actions">
        <button className="button button-outline-dark" type="button" onClick={()=>save(defaultPreferences)}>Necessary only</button>
        {customizing ? <button className="button button-primary" type="button" onClick={()=>save(preferences)}>Save choices</button> : <button className="button button-primary" type="button" onClick={()=>save({necessary:true,analytics:true})}>Accept optional analytics</button>}
        {!customizing && <button className="consent-text-button" type="button" onClick={()=>setCustomizing(true)}>Customize</button>}
        <a href="/privacy">Privacy policy</a>
      </div>
    </div>
    {readPreferences() && <button className="consent-close" type="button" aria-label="Close privacy choices" onClick={()=>setVisible(false)}><X size={20}/></button>}
  </aside>;
}
